import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    HeartIcon,
    LightBulbIcon,
    GlobeAmericasIcon,
    ScaleIcon,
    UserGroupIcon,
    ArrowPathIcon,
    BeakerIcon,
    ChatBubbleLeftRightIcon,
    ArrowRightIcon,
    ShieldCheckIcon,
    StarIcon,
    TruckIcon
} from '@heroicons/react/24/outline';
import SEO from '../../components/SEO';

const About = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            <SEO
                title="About Us - Marvelous Agri Solution"
                description="Learn about Marvelous Agri Solution's mission to bridge the gap between science and farming. We provide sustainable and profitable agricultural solutions."
            />
            {/* Header Banner - BigHaat Style */}
            <section className="bg-gradient-to-r from-green-600 to-green-700 text-white py-12 md:py-16">
                <div className="container mx-auto px-6">
                    <nav className="text-sm mb-4">
                        <Link to="/" className="text-green-200 hover:text-white">Home</Link>
                        <span className="mx-2">/</span>
                        <span>About Us</span>
                    </nav>
                    <h1 className="text-3xl md:text-4xl font-bold">About Us</h1>
                    <p className="text-green-100 mt-2 max-w-2xl">Bridging Science & Farming for a sustainable future</p>
                </div>
            </section>

            {/* Overview Section */}
            <section className="py-12 bg-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            variants={fadeInUp}
                        >
                            <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">Company Overview</span>
                            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">Bridging Science & Farming</h2>
                            <p className="text-gray-600 text-base leading-relaxed mb-4">
                                Marvelous Agri Solution is a next-generation Agri-Tech innovation company redefining how agriculture performs in the modern world.
                            </p>
                            <p className="text-gray-600 text-base leading-relaxed mb-4">
                                We combine agricultural science, intelligent data systems, regenerative practices, and precision execution to create high-performance farming ecosystems that are scalable, resilient, and future-ready.
                            </p>
                            <p className="text-base leading-relaxed mb-4 text-green-700 font-semibold italic">
                                We believe farming is not just cultivation — it is a living system powered by intelligence.
                            </p>

                            <h3 className="text-xl font-bold text-gray-800 mt-6 mb-3">Where Agriculture Meets Advanced Intelligence</h3>
                            <p className="text-gray-600 leading-relaxed mb-3">Our work integrates:</p>
                            <ul className="list-none space-y-2 mb-4 text-gray-600">
                                {["Real-time Farm Data Intelligence", "AI-Enabled Crop Advisory Systems", "Precision Soil & Nutrient Optimization", "Climate-Smart Cultivation Models", "Smart Irrigation Engineering", "Integrated Pest & Crop Health Architecture", "Residue-Free Sustainable Production Systems"].map((item, idx) => (
                                    <li key={idx} className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <p className="text-gray-600 text-base leading-relaxed mb-6 font-medium">
                                Every farm becomes a connected, optimized, and continuously improving growth platform.
                            </p>

                            <h3 className="text-xl font-bold text-gray-800 mt-6 mb-3">Innovation with Purpose</h3>
                            <p className="text-gray-600 text-base leading-relaxed mb-4">
                                At Marvelous Agri Solution, technology serves biology — not the other way around.
                            </p>
                            <p className="text-gray-600 leading-relaxed mb-3">We design systems that:</p>
                            <div className="flex flex-wrap gap-2 mb-6">
                                {["Increase productivity", "Enhance soil vitality", "Optimize input efficiency", "Improve farmer profitability", "Strengthen food quality and safety"].map((item, idx) => (
                                    <span key={idx} className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium border border-green-100">
                                        {item}
                                    </span>
                                ))}
                            </div>
                            <p className="text-gray-600 text-base leading-relaxed mb-8 italic">
                                Our solutions are built to empower farmers with clarity, confidence, and control.
                            </p>

                            <div className="bg-gray-50 border-l-4 border-green-600 p-6 rounded-r-xl mb-6 shadow-sm">
                                <h4 className="text-lg font-bold text-gray-900 mb-2">Powered by Science. Driven by Impact.</h4>
                                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                                    Born from a passion for precision farming, we operate at the intersection of agronomy, analytics, engineering, and local field expertise.
                                </p>
                                <p className="text-gray-800 font-semibold mb-3">
                                    We don’t believe in incremental improvement.<br />
                                    We believe in exponential agricultural advancement.
                                </p>
                                <p className="text-gray-600 text-sm leading-relaxed">
                                    By aligning sustainability with profitability, we enable farms to grow stronger each season — economically, environmentally, and technologically.
                                </p>
                            </div>
                            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition">
                                Contact Us <ArrowRightIcon className="w-4 h-4" />
                            </Link>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="sticky top-24 self-start"
                        >
                            <div className="absolute -inset-4 bg-green-100 rounded-2xl z-0"></div>
                            <img
                                src={require('../../assets/newimages/mas website about us.webp')}
                                alt="Bridging Science & Farming"
                                className="relative z-10 rounded-2xl shadow-xl w-full object-cover h-[350px]"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission */}
            <section className="py-12 bg-gradient-to-r from-green-700 to-green-800 text-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                            <LightBulbIcon className="w-10 h-10 text-yellow-400 mb-4" />
                            <h2 className="text-2xl font-bold mb-4">Our Vision</h2>
                            <p className="text-green-100 leading-relaxed text-sm">
                                Our vision is to build the world’s most intelligent and farmer-empowered agricultural ecosystem, where science, sustainability, and scalable innovation operate as one unified force. We envision transforming farms into high-performance growth platforms powered by precision, data-driven decision systems, and resilient cultivation models. By elevating farmers into confident agri-entrepreneurs and digitally strengthening the entire agricultural value chain, we aim to accelerate responsible technology adoption and sustainable trade. Our ultimate aspiration is to create a future where productivity, profitability, and environmental harmony advance together — securing food systems for generations to come.
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20">
                            <GlobeAmericasIcon className="w-10 h-10 text-blue-400 mb-4" />
                            <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
                            <ul className="space-y-2 text-green-100">
                                {[
                                    "To integrate science, policy, and innovation into one powerful agricultural ecosystem.",
                                    "To empower farmers with intelligent, scalable, and profitability-driven solutions.",
                                    "To strengthen the value chain through technology and strategic trade.",
                                    "To advance food security alongside environmental sustainability.",
                                    "To transform agriculture into a resilient engine of global growth."
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 mt-2 bg-green-400 rounded-full"></div>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Values */}
            <section className="py-12 bg-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="text-center mb-10">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">What Drives Us</span>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Core Values</h2>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                        {[
                            { title: "Accountability", icon: <ScaleIcon className="w-6 h-6" /> },
                            { title: "Integrity", icon: <ShieldCheckIcon className="w-6 h-6" /> },
                            { title: "Transparency", icon: <LightBulbIcon className="w-6 h-6" /> },
                            { title: "Farmers First", icon: <UserGroupIcon className="w-6 h-6" /> },
                            { title: "Quality", icon: <StarIcon className="w-6 h-6" /> },
                            { title: "Sustainability", icon: <GlobeAmericasIcon className="w-6 h-6" /> },
                            { title: "Innovation", icon: <BeakerIcon className="w-6 h-6" /> },
                            { title: "Efficiency", icon: <ArrowPathIcon className="w-6 h-6" /> },
                            { title: "Agility", icon: <TruckIcon className="w-6 h-6" /> },
                            { title: "Safety", icon: <ShieldCheckIcon className="w-6 h-6" /> },
                            { title: "Customer Centricity", icon: <ChatBubbleLeftRightIcon className="w-6 h-6" /> },
                            { title: "Respect for People", icon: <HeartIcon className="w-6 h-6" /> },
                        ].map((val, i) => (
                            <motion.div
                                key={i}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true }}
                                variants={fadeInUp}
                                className="text-center p-4 rounded-xl hover:bg-green-50 transition"
                            >
                                <div className="w-14 h-14 mx-auto bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-4">
                                    {val.icon}
                                </div>
                                <h3 className="font-bold text-gray-900 mb-2">{val.title}</h3>
                                <p className="text-gray-600 text-sm">{val.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Approach */}
            <section className="py-12 bg-gray-900 text-white">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">Farmer-First Model</h2>
                        <p className="text-gray-400">Our proven 4-Step success approach</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {[
                            { step: "01", title: "Understand", desc: "Soil, crop, climate, challenges analysis." },
                            { step: "02", title: "Design", desc: "Customized advisory & input planning." },
                            { step: "03", title: "Implement", desc: "On-field support & digital monitoring." },
                            { step: "04", title: "Evaluate", desc: "Continuous learning & optimization." },
                        ].map((item, i) => (
                            <div key={i} className="relative p-6 border border-white/10 rounded-xl bg-white/5 hover:border-green-500/50 transition">
                                <div className="text-4xl font-black text-white/5 absolute top-2 right-4">{item.step}</div>
                                <ArrowPathIcon className="w-8 h-8 text-green-500 mb-4" />
                                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                <p className="text-gray-400 text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Philosophy */}
            <section className="py-16 bg-white relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-green-50 rounded-bl-full -mr-10 -mt-10 opacity-60"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-50 rounded-tr-full -ml-8 -mb-8 opacity-60"></div>

                <div className="container mx-auto px-6 max-w-5xl relative z-10">
                    <div className="text-center mb-12">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">Our Philosophy</span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
                            Healthy & Wealthy <span className="text-green-600">Agriculture</span>
                        </h2>
                    </div>

                    <div className="bg-white rounded-3xl p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 mb-10">
                        <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6 font-medium text-center max-w-4xl mx-auto">
                            At Marvelous Agri Solution, we believe the future of agriculture lies in creating living ecosystems that generate both biological excellence and economic acceleration. A truly successful farm is not measured only by yield — but by the vitality of its soil, the purity of its produce, the resilience of its ecosystem, and the prosperity of its farmer.
                        </p>

                        <div className="text-center max-w-3xl mx-auto mb-10">
                            <p className="text-gray-600 leading-relaxed italic text-lg border-l-4 border-green-500 pl-4 py-2 bg-green-50/50 rounded-r-lg">
                                <span className="font-bold">“Healthy & Wealthy Agriculture”</span> is our integrated growth framework — a model where soil biology, plant performance, environmental balance, and farmer profitability scale together in one synchronized system.
                            </p>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">We engineer regenerative farming architectures that:</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                            {[
                                "Activate soil intelligence",
                                "Minimize chemical dependency",
                                "Optimize every drop of water and every unit of input",
                                "Deliver residue-conscious, high-quality food",
                                "Increase farmer income sustainably and consistently",
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 bg-gray-50 p-6 rounded-2xl hover:bg-green-50 transition-colors duration-300">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                                        <div className="w-2.5 h-2.5 bg-green-600 rounded-full"></div>
                                    </div>
                                    <p className="text-gray-700 font-medium">{item}</p>
                                </div>
                            ))}
                        </div>

                        <div className="text-center space-y-3">
                            <p className="text-xl text-gray-800">For us, sustainability is not a limitation — <span className="font-bold text-green-700">it is a multiplier.</span></p>
                            <p className="text-xl text-gray-800">Profitability is not short-term gain — <span className="font-bold text-green-700">it is long-term strength.</span></p>
                        </div>
                    </div>

                    <div className="bg-gray-900 text-white rounded-3xl p-10 text-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-green-900/20 z-0"></div>
                        <div className="relative z-10">
                            <p className="text-2xl font-light italic text-green-100 mb-8">
                                We are building farms that grow stronger each season — biologically, economically, and technologically.
                            </p>

                            <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 font-bold text-xl md:text-2xl">
                                <span>Healthy <span className="text-green-400">ecosystems.</span></span>
                                <span className="hidden md:block w-2 h-2 bg-white rounded-full"></span>
                                <span>Wealth-generating <span className="text-green-400">farms.</span></span>
                                <span className="hidden md:block w-2 h-2 bg-white rounded-full"></span>
                                <span>Future-secured <span className="text-green-400">agriculture.</span></span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Quote Section */}
            <section className="py-12 bg-green-50">
                <div className="container mx-auto px-6 max-w-3xl text-center">
                    <ChatBubbleLeftRightIcon className="w-10 h-10 text-green-600 mx-auto mb-4 opacity-50" />
                    <blockquote className="text-xl md:text-2xl font-serif text-gray-800 italic leading-relaxed mb-6">
                        "Our goal is simple—make farming smarter, safer, and more profitable while respecting nature. When farmers grow, the nation grows."
                    </blockquote>
                    <div className="flex items-center justify-center gap-3">
                        <div className="w-8 h-0.5 bg-green-600"></div>
                        <span className="font-bold text-gray-900 uppercase tracking-widest text-xs">Leadership Message</span>
                        <div className="w-8 h-0.5 bg-green-600"></div>
                    </div>
                </div>
            </section>

            {/* Careers Section - NEW */}
            <section className="py-16 bg-white overflow-hidden relative">
                {/* Background Decor */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-green-50 rounded-bl-full -mr-20 -mt-20 opacity-50"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-50 rounded-tr-full -ml-16 -mb-16 opacity-50"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="text-center mb-16 max-w-3xl mx-auto">
                        <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">CAREERS</span>
                        <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Build the Future of <br /><span className="text-green-600">Agriculture With Us</span></h2>
                        <p className="text-xl text-gray-600 leading-relaxed font-light">
                            At Marvelous Agri Solution, we are not just building technology. <br />
                            We are building the future of farming.
                        </p>
                        <p className="mt-6 text-gray-500">
                            We are looking for bold thinkers, field innovators, satellite analysts, agronomists, and technology creators who believe agriculture deserves world-class solutions.
                        </p>
                    </div>

                    {/* Why Work With Us */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
                        {[
                            { title: "Work on Real Impact", desc: "Your work directly improves farmer income and crop productivity.", icon: <HeartIcon className="w-8 h-8 text-red-500" /> },
                            { title: "Innovation at Scale", desc: "Be part of cutting-edge satellite intelligence and agri-data systems.", icon: <GlobeAmericasIcon className="w-8 h-8 text-blue-500" /> },
                            { title: "Culture of Ownership", desc: "We believe in responsibility, leadership, and growth.", icon: <UserGroupIcon className="w-8 h-8 text-green-500" /> },
                            { title: "Fast-Growing Global Vision", desc: "We are expanding across borders. Grow with us.", icon: <ArrowRightIcon className="w-8 h-8 text-orange-500" /> },
                        ].map((item, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-lg transition-shadow text-center group">
                                <div className="w-16 h-16 mx-auto bg-gray-50 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    {item.icon}
                                </div>
                                <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                                <p className="text-gray-500 text-sm">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Who We Are Looking For */}
                    <div className="bg-gray-900 rounded-3xl p-8 md:p-16 text-center text-white relative overflow-hidden shadow-2xl">
                        <div className="absolute inset-0 bg-green-900/20 z-0"></div>
                        <div className="relative z-10">
                            <h3 className="text-2xl md:text-3xl font-bold mb-8">Who We Are Looking For</h3>
                            <div className="flex flex-wrap justify-center gap-4 mb-12">
                                {[
                                    "Agricultural graduates & agronomists",
                                    "Agri-tech innovators",
                                    "Field officers & crop advisors",
                                    "Sales & distribution professionals",
                                    "Data & precision agriculture specialists",
                                    "Supply chain & procurement experts",
                                    "Food processing & value chain professionals"
                                ].map((role, idx) => (
                                    <span key={idx} className="px-5 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-green-100 font-medium hover:bg-white/20 transition cursor-default">
                                        {role}
                                    </span>
                                ))}
                            </div>

                            <div className="max-w-2xl mx-auto border-t border-white/10 pt-8">
                                <p className="text-xl md:text-2xl font-light text-green-200 mb-8 italic">
                                    "If you are passionate about transforming agriculture with intelligence — your journey starts here."
                                </p>
                                <Link to="/contact" className="inline-block px-10 py-4 bg-green-600 hover:bg-green-500 text-white font-bold rounded-full transition shadow-lg hover:shadow-green-500/30 transform hover:-translate-y-1">
                                    Join Our Team
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default About;
